import { Component, OnInit, OnDestroy, signal, computed, inject, effect, untracked } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { Observable, Subject, map } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { BreadcrumbService, EnumValues } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import ObjectID from 'bson-objectid';
import { EnumService } from '../../../services/enum.service';
import { UnitOfMeasure } from '../../../models/inventory/inventory-adjustment';
import { SecondQualityDispatchProxyService } from '../../../proxies/second-quality-dispatch.proxy.service';
import { Business, Address, BusinessEntityType, AddressType } from '../../../models/finance/business';
import { ItemMaster } from '../../../models/item/item-master';
import {
  SecondQualityDispatch,
  SecondQualityDispatchStatus,
  ItemLineBGradeInstruction,
} from '../../../models/second-quality-dispatch/second-quality-dispatch';
import {
  SecondQualityDispatchInsertCommand,
  SecondQualityDispatchShippingUpdateCommand,
  SecondQualityDispatchBillingUpdateCommand,
  SecondQualityDispatchItemLinesUpdateCommand,
  SecondQualityDispatchRollPriceUpdateCommand,
  SecondQualityDispatchLotItemPriceUpdateCommand,
} from '../../../models/second-quality-dispatch/second-quality-dispatch-commands';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { UserGroup } from '../../../models/authorization/enum/user-group.enum';
import { ItemMasterProxyService } from '../../../proxies/item/item-master.proxy.service';
import { BusinessProxyService } from '../../../proxies/finance/business.proxy.service';
import { LotItem } from '@app/models/inventory';

interface ItemLineForm {
  lineId: FormControl<string | null>;
  itemCode: FormControl<string>;
  itemDescription: FormControl<string>;
  quantity: FormControl<number>;
  uom: FormControl<number | null>;
  packageCount: FormControl<number>;
  unitPrice: FormControl<number>;
}

@Component({
  selector: 'app-second-quality-dispatch-details',
  templateUrl: './second-quality-dispatch-details.component.html',
  styleUrls: ['./second-quality-dispatch-details.component.scss'],
})
export class SecondQualityDispatchDetailsComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  // Injected services
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly enumService = inject(EnumService);
  private readonly translate = inject(TranslateService);
  private readonly proxyService = inject(SecondQualityDispatchProxyService);
  private readonly businessProxyService = inject(BusinessProxyService);
  private readonly itemMasterProxyService = inject(ItemMasterProxyService);
  private readonly breadcrumbService = inject(BreadcrumbService);

  // State signals
  sqd = signal<SecondQualityDispatch | null>(null);
  isCreateMode = signal(false);
  isLoading = signal(false);
  scanInput = signal('');
  lotItemScanInput = signal('');

  // Enum signals
  sqdStatuses = signal<EnumValues>({});
  shipmentTypes = signal<EnumValues>({});
  paymentTerms = signal<EnumValues>({});

  // Business autocomplete
  selectedItemForLine = signal<ItemMaster | null>(null);

  // Selected businesses for address resolution
  selectedClient = signal<Business | null>(null);
  selectedShippingTo = signal<Business | null>(null);
  selectedBillTo = signal<Business | null>(null);

  // Computed addresses
  readonly shippingAddresses = computed<Address[]>(() => {
    const business = this.selectedShippingTo() ?? this.selectedClient();
    if (!business?.addresses) return [];
    return business.addresses.filter((addr) => addr.addressTypes?.includes(AddressType.Shipping)) ?? [];
  });

  readonly billingAddresses = computed<Address[]>(() => {
    const business = this.selectedBillTo() ?? this.selectedClient();
    if (!business?.addresses) return [];
    return business.addresses.filter((addr) => addr.addressTypes?.includes(AddressType.Billing)) ?? [];
  });

  // Computed from SQD state
  readonly isDraft = computed(() => this.sqd()?.status === SecondQualityDispatchStatus.Draft);
  readonly isCompleted = computed(() => this.sqd()?.status === SecondQualityDispatchStatus.Completed);
  readonly isCancelled = computed(() => this.sqd()?.status === SecondQualityDispatchStatus.Cancelled);

  readonly canEdit = computed(() => {
    return AuthService.hasRole(UserGroup.SecondQualityDispatchEditor) || AuthService.hasRole(UserGroup.SecondQualityDispatchAdmin);
  });

  readonly isEditable = computed(() => this.isDraft() && this.canEdit());

  readonly totalRollWeight = computed(() => {
    const rolls = this.sqd()?.scannedRolls ?? [];
    return rolls.reduce((sum, r) => sum + (r.finalWeight?.weightInLbs ?? 0), 0);
  });

  readonly totalLotItemWeight = computed(() => {
    const lotItems = this.sqd()?.scannedLotItems ?? [];
    return lotItems.reduce((sum, li) => sum + (li.netQuantity ?? 0), 0);
  });

  // Breadcrumb

  // UOM options for dropdown
  readonly uomOptions = Object.keys(UnitOfMeasure)
    .filter((key) => isNaN(Number(key)))
    .map((key) => ({ label: key, value: UnitOfMeasure[key as keyof typeof UnitOfMeasure] }));

  // === FORMS ===

  // Initial creation form
  initialForm = new FormGroup({
    client: new FormControl<Business | null>(null, [Validators.required]),
    shipToAddress: new FormControl<Address | null>(null, [Validators.required]),
    shipmentType: new FormControl<number | null>(null, [Validators.required]),
    shippingTo: new FormControl<Business | null>(null),
    billTo: new FormControl<Business | null>(null),
    billToAddress: new FormControl<Address | null>(null),
    paymentTermClientCode: new FormControl<string>('', [Validators.required]),
  });

  // Shipping info form (for editing existing)
  shippingForm = new FormGroup({
    shippingTo: new FormControl<Business | null>(null),
    shipToAddress: new FormControl<Address | null>(null, [Validators.required]),
    shipmentType: new FormControl<number | null>(null, [Validators.required]),
  });

  // Billing info form (for editing existing)
  billingForm = new FormGroup({
    billTo: new FormControl<Business | null>(null),
    billToAddress: new FormControl<Address | null>(null),
    paymentTermClientCode: new FormControl<string>('', [Validators.required]),
    invoiceComment: new FormControl<string>(''),
  });

  // Roll price form
  rollPriceForm = new FormGroup({
    scannedRollsUnitPrice: new FormControl<number | null>(null, [Validators.required]),
    scannedRollsItemDescription: new FormControl<string>('', { nonNullable: true }),
  });

  // Lot item price form
  lotItemPriceForm = new FormGroup({
    scannedLotItemsUnitPrice: new FormControl<number | null>(null, [Validators.required]),
    scannedLotItemsItemDescription: new FormControl<string>('', { nonNullable: true }),
  });

  // Item lines form array
  itemLinesForm = new FormArray<FormGroup<ItemLineForm>>([]);

  constructor() {
    // Preload enums
    const statusesValue = this.enumService.getEnum('SecondQualityDispatchStatus', 'inventory').values;
    const shipmentTypesValue = this.enumService.getEnum('ShipmentType', 'order').values;
    const paymentTermsValue = this.enumService.getEnum('PaymentTermClient', 'inventory').values;

    if (Object.keys(statusesValue).length > 0) this.sqdStatuses.set(statusesValue);
    if (Object.keys(shipmentTypesValue).length > 0) this.shipmentTypes.set(shipmentTypesValue);
    if (Object.keys(paymentTermsValue).length > 0) this.paymentTerms.set(paymentTermsValue);

    // Non-editable states keep the same inputs, just disabled, for visual consistency
    effect(() => {
      const editable = this.isEditable();
      untracked(() => {
        for (const form of [this.shippingForm, this.billingForm]) {
          if (editable) {
            form.enable({ emitEvent: false });
          } else {
            form.disable({ emitEvent: false });
          }
        }
      });
    });
  }

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      if (params['mode'] === 'create') {
        this.isCreateMode.set(true);
        this.breadcrumbService.setCurrent('Common_New');
      } else if (params['id']) {
        this.loadSqd(params['id']);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // === ENUM HELPERS ===

  getShipmentTypeOptions(): { value: number; label: string }[] {
    const types = this.shipmentTypes();
    return Object.keys(types)
      .filter((key) => !isNaN(Number(key)))
      .map((key) => ({ value: Number(key), label: types[Number(key)] }));
  }

  getPaymentTermOptions(): { value: string; label: string }[] {
    const terms = this.paymentTerms();
    return Object.keys(terms)
      .filter((key) => !isNaN(Number(key)))
      .map((key) => ({ value: key, label: terms[Number(key)] }));
  }

  getStatusLabel(status: SecondQualityDispatchStatus): string {
    const statuses = this.sqdStatuses();
    return statuses[status] || status.toString();
  }

  getStatusSeverity(status: SecondQualityDispatchStatus): 'success' | 'info' | 'warn' | 'secondary' | 'danger' | undefined {
    switch (status) {
      case SecondQualityDispatchStatus.Draft:
        return 'secondary';
      case SecondQualityDispatchStatus.Completed:
        return 'success';
      case SecondQualityDispatchStatus.Cancelled:
        return 'danger';
      default:
        return 'secondary';
    }
  }

  // === LOAD ===

  loadSqd(id: string): void {
    this.isLoading.set(true);
    this.proxyService
      .getById(id)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (result) => {
          if (result?.data) {
            this.sqd.set(result.data);
            this.breadcrumbService.setCurrent(result.data.number);
            this.isCreateMode.set(false);
            this.populateFormsFromSqd(result.data);
          }
        },
      });
  }

  private populateFormsFromSqd(sqd: SecondQualityDispatch): void {
    // Populate roll price
    this.rollPriceForm.patchValue({
      scannedRollsUnitPrice: sqd.scannedRollsUnitPrice ?? null,
      scannedRollsItemDescription: sqd.scannedRollsItemDescription ?? '',
    });

    // Populate lot item price
    this.lotItemPriceForm.patchValue({
      scannedLotItemsUnitPrice: sqd.scannedLotItemsUnitPrice ?? null,
      scannedLotItemsItemDescription: sqd.scannedLotItemsItemDescription ?? '',
    });

    // Populate billing form
    this.billingForm.patchValue({
      paymentTermClientCode: sqd.paymentTermClientCode || '',
      invoiceComment: sqd.invoiceComment || '',
    });

    // Populate item lines
    this.itemLinesForm.clear();
    for (const line of sqd.lines ?? []) {
      this.itemLinesForm.push(this.createItemLineGroup(line));
    }

    // When no separate reference, the API returns the client as shippingTo/billTo.
    // Set selectedClient so the computed address options (which fall back to client) work.
    const clientBusiness = (!sqd.shippingToReference && sqd.shippingTo) || (!sqd.billToReference && sqd.billTo);
    if (clientBusiness) {
      this.selectedClient.set(clientBusiness);
    }

    // Resolve shipping — only set shippingTo form field if a separate reference exists
    if (sqd.shippingToReference && sqd.shippingTo) {
      this.selectedShippingTo.set(sqd.shippingTo);
      this.shippingForm.patchValue({ shippingTo: sqd.shippingTo });
    }

    // Resolve ship-to address (from client or separate shippingTo addresses)
    const shipSource = sqd.shippingToReference ? sqd.shippingTo : this.selectedClient();
    const shipAddr = shipSource?.addresses?.find((a) => a.code === Number(sqd.shipToAddressCode));
    if (shipAddr) {
      this.shippingForm.patchValue({ shipToAddress: shipAddr });
    }

    // Resolve billing — only set billTo form field if a separate reference exists
    if (sqd.billToReference && sqd.billTo) {
      this.selectedBillTo.set(sqd.billTo);
      this.billingForm.patchValue({ billTo: sqd.billTo });
    }

    // Resolve bill-to address (from client or separate billTo addresses)
    const billSource = sqd.billToReference ? sqd.billTo : this.selectedClient();
    const billAddr = billSource?.addresses?.find((a) => a.code === Number(sqd.billToAddressCode));
    if (billAddr) {
      this.billingForm.patchValue({ billToAddress: billAddr });
    }

    this.shippingForm.patchValue({
      shipmentType: sqd.shipmentType,
    });
  }

  private createItemLineGroup(line?: Partial<ItemLineBGradeInstruction>): FormGroup<ItemLineForm> {
    return new FormGroup<ItemLineForm>({
      lineId: new FormControl<string | null>(line?.lineId ?? new ObjectID().toHexString()),
      itemCode: new FormControl<string>(
        { value: line?.itemCode ?? '', disabled: true },
        { nonNullable: true, validators: [Validators.required] },
      ),
      itemDescription: new FormControl<string>({ value: line?.itemDescription ?? '', disabled: true }, { nonNullable: true }),
      quantity: new FormControl<number>(line?.quantity ?? 0, {
        nonNullable: true,
        validators: [Validators.required, Validators.min(0.01)],
      }),
      uom: new FormControl<number | null>({ value: line?.uom ?? null, disabled: true }),
      packageCount: new FormControl<number>(line?.packageCount ?? 0, {
        nonNullable: true,
        validators: [Validators.required, Validators.min(1)],
      }),
      unitPrice: new FormControl<number>(line?.unitPrice ?? 0, { nonNullable: true, validators: [Validators.required, Validators.min(0)] }),
    });
  }

  // === AUTOCOMPLETE ===

  public fetchClients = (query: string): Observable<Business[]> =>
    this.businessProxyService
      .search({ name: query, businessTypes: [BusinessEntityType.Client] })
      .pipe(map((result) => result?.data?.items ?? []));

  public fetchShippingTo = (query: string): Observable<Business[]> =>
    this.businessProxyService
      .search({ name: query, businessTypes: [BusinessEntityType.Client, BusinessEntityType.Consignee] })
      .pipe(map((result) => result?.data?.items ?? []));

  public fetchBillTo = (query: string): Observable<Business[]> =>
    this.businessProxyService
      .search({ name: query, businessTypes: [BusinessEntityType.Client] })
      .pipe(map((result) => result?.data?.items ?? []));

  public fetchItems = (query: string): Observable<ItemMaster[]> =>
    this.itemMasterProxyService.search({ itemCode: query, name: query }, true).pipe(map((result) => result?.data?.items ?? []));

  // Client selection handlers
  onClientSelected(business: Business): void {
    this.selectedClient.set(business);
    // Reset address selection
    this.initialForm.controls.shipToAddress.reset();
  }

  onInitialShippingToSelected(business: Business): void {
    this.selectedShippingTo.set(business);
  }

  onInitialBillToSelected(business: Business): void {
    this.selectedBillTo.set(business);
  }

  onShippingToSelected(business: Business): void {
    this.selectedShippingTo.set(business);
    this.shippingForm.controls.shipToAddress.reset();
  }

  onBillToSelected(business: Business): void {
    this.selectedBillTo.set(business);
    this.billingForm.controls.billToAddress.reset();
  }

  getItemDisplayLabel(item: ItemMaster): string {
    const lang = this.translate.currentLang || 'es';
    const name = item.itemDisplayName?.[lang as keyof typeof item.itemDisplayName] || item.itemLongDisplayName || '';
    return name as string;
  }

  onItemSelectForLine(item: ItemMaster): void {
    if (!item) return;
    const lang = this.translate.currentLang || 'es';
    const description =
      (item.itemDescription?.[lang as keyof typeof item.itemDescription] as string) || item.itemLongDisplayName || item.itemCode;
    this.itemLinesForm.push(
      this.createItemLineGroup({
        itemCode: item.itemCode,
        itemDescription: description,
        uom: item.unitOfMeasure,
      }),
    );
    this.selectedItemForLine.set(null);
  }

  // === CREATE ===

  createSqd(): void {
    if (this.initialForm.invalid) {
      this.initialForm.markAllAsTouched();
      return;
    }

    const formVal = this.initialForm.value;
    const client = formVal.client as Business;
    const shipToAddr = formVal.shipToAddress as Address;
    const shippingTo = formVal.shippingTo as Business | null;
    const billTo = formVal.billTo as Business | null;
    const billToAddr = formVal.billToAddress as Address | null;

    const command: SecondQualityDispatchInsertCommand = {
      clientReference: client.id!,
      shipToAddressCode: shipToAddr.code.toString(),
      shipmentType: formVal.shipmentType!,
      shippingToReference: shippingTo?.id ?? undefined,
      billToReference: billTo?.id ?? undefined,
      billToAddressCode: billToAddr?.code.toString() ?? undefined,
      paymentTermClientCode: formVal.paymentTermClientCode!,
    };

    this.isLoading.set(true);
    this.proxyService
      .create(command)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (result) => {
          if (result?.data) {
            GlobalMessageUtil.success('Success', 'Second quality dispatch created successfully');
            this.router.navigate(['/second-quality-dispatch-details'], { queryParams: { id: result.data.id } });
          }
        },
      });
  }

  // === SCAN ROLL ===

  scanRoll(): void {
    const rollNumber = this.scanInput().trim();
    if (!rollNumber || !this.sqd()) return;

    this.isLoading.set(true);
    this.proxyService
      .scanRoll(this.sqd()!.id, rollNumber)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (result) => {
          if (result?.data) {
            this.sqd.set(result.data);
            this.scanInput.set('');
            GlobalMessageUtil.success('Success', `Roll ${rollNumber} scanned successfully`);
          }
        },
      });
  }

  removeRoll(rollNumber: string): void {
    this.confirmationService.confirm({
      message: `Remove roll ${rollNumber}?`,
      accept: () => {
        this.isLoading.set(true);
        this.proxyService
          .removeRoll(this.sqd()!.id, rollNumber)
          .pipe(finalize(() => this.isLoading.set(false)))
          .subscribe({
            next: (result) => {
              if (result?.data) {
                this.sqd.set(result.data);
                GlobalMessageUtil.success('Success', `Roll ${rollNumber} removed`);
              }
            },
          });
      },
    });
  }

  // === ROLL PRICE ===

  saveRollPrice(): void {
    if (this.rollPriceForm.invalid) {
      this.rollPriceForm.markAllAsTouched();
      return;
    }

    const command: SecondQualityDispatchRollPriceUpdateCommand = {
      scannedRollsUnitPrice: this.rollPriceForm.value.scannedRollsUnitPrice!,
      scannedRollsItemDescription: this.rollPriceForm.value.scannedRollsItemDescription || undefined,
    };

    this.isLoading.set(true);
    this.proxyService
      .updateRollPrice(this.sqd()!.id, command)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (result) => {
          if (result?.data) {
            this.sqd.set(result.data);
            GlobalMessageUtil.success('Success', 'Roll price updated');
          }
        },
      });
  }

  // === LOT ITEM SCANNING ===

  scanLotItem(): void {
    const lotItemNumber = this.lotItemScanInput().trim();
    if (!lotItemNumber || !this.sqd()) return;

    this.isLoading.set(true);
    this.proxyService
      .scanLotItem(this.sqd()!.id, lotItemNumber)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (result) => {
          if (result?.data) {
            this.sqd.set(result.data);
            this.lotItemScanInput.set('');
            GlobalMessageUtil.success('Success', `Lote ${lotItemNumber} agregado exitosamente`);
          }
        },
      });
  }

  getStrictLotItem(unknown: unknown): LotItem {
    return unknown as LotItem;
  }

  removeLotItem(lotItemNumber: string): void {
    this.confirmationService.confirm({
      message: `¿Remover lote ${lotItemNumber}?`,
      accept: () => {
        this.isLoading.set(true);
        this.proxyService
          .removeLotItem(this.sqd()!.id, lotItemNumber)
          .pipe(finalize(() => this.isLoading.set(false)))
          .subscribe({
            next: (result) => {
              if (result?.data) {
                this.sqd.set(result.data);
                GlobalMessageUtil.success('Success', `Lote ${lotItemNumber} removido`);
              }
            },
          });
      },
    });
  }

  // === LOT ITEM PRICE ===

  saveLotItemPrice(): void {
    if (this.lotItemPriceForm.invalid) {
      this.lotItemPriceForm.markAllAsTouched();
      return;
    }

    const command: SecondQualityDispatchLotItemPriceUpdateCommand = {
      scannedLotItemsUnitPrice: this.lotItemPriceForm.value.scannedLotItemsUnitPrice!,
      scannedLotItemsItemDescription: this.lotItemPriceForm.value.scannedLotItemsItemDescription || undefined,
    };

    this.isLoading.set(true);
    this.proxyService
      .updateLotItemPrice(this.sqd()!.id, command)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (result) => {
          if (result?.data) {
            this.sqd.set(result.data);
            GlobalMessageUtil.success('Success', 'Precio de lotes actualizado');
          }
        },
      });
  }

  // === ITEM LINES ===

  addItemLine(): void {
    this.itemLinesForm.push(this.createItemLineGroup());
  }

  removeItemLine(index: number): void {
    this.itemLinesForm.removeAt(index);
  }

  saveItemLines(): void {
    if (this.itemLinesForm.invalid) {
      this.itemLinesForm.markAllAsTouched();
      return;
    }

    const lines: ItemLineBGradeInstruction[] = this.itemLinesForm.controls.map((group) => {
      const raw = group.getRawValue();
      return {
        lineId: raw.lineId || undefined,
        itemCode: raw.itemCode,
        itemDescription: raw.itemDescription || undefined,
        quantity: raw.quantity,
        uom: raw.uom ?? 0,
        packageCount: raw.packageCount,
        unitPrice: raw.unitPrice,
      } as ItemLineBGradeInstruction;
    });

    const command: SecondQualityDispatchItemLinesUpdateCommand = { lines };

    this.isLoading.set(true);
    this.proxyService
      .updateItemLines(this.sqd()!.id, command)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (result) => {
          if (result?.data) {
            this.sqd.set(result.data);
            this.populateFormsFromSqd(result.data);
            GlobalMessageUtil.success('Success', 'Item lines updated');
          }
        },
      });
  }

  // === SHIPPING & BILLING ===

  saveShippingInfo(): void {
    if (this.shippingForm.invalid) {
      this.shippingForm.markAllAsTouched();
      return;
    }

    const formVal = this.shippingForm.value;
    const shippingTo = formVal.shippingTo as Business | null;
    const shipToAddr = formVal.shipToAddress as Address;

    const command: SecondQualityDispatchShippingUpdateCommand = {
      shippingToReference: shippingTo?.id ?? undefined,
      shipToAddressCode: shipToAddr.code.toString(),
      shipmentType: formVal.shipmentType!,
    };

    this.isLoading.set(true);
    this.proxyService
      .updateShippingInfo(this.sqd()!.id, command)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (result) => {
          if (result?.data) {
            this.sqd.set(result.data);
            GlobalMessageUtil.success('Success', 'Shipping info updated');
          }
        },
      });
  }

  saveBillingInfo(): void {
    if (this.billingForm.invalid) {
      this.billingForm.markAllAsTouched();
      return;
    }

    const formVal = this.billingForm.value;
    const billTo = formVal.billTo as Business | null;
    const billToAddr = formVal.billToAddress as Address | null;

    const command: SecondQualityDispatchBillingUpdateCommand = {
      billToReference: billTo?.id ?? undefined,
      billToAddressCode: billToAddr?.code.toString() ?? undefined,
      invoiceComment: formVal.invoiceComment || undefined,
      paymentTermClientCode: formVal.paymentTermClientCode!,
    };

    this.isLoading.set(true);
    this.proxyService
      .updateBillingInfo(this.sqd()!.id, command)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (result) => {
          if (result?.data) {
            this.sqd.set(result.data);
            GlobalMessageUtil.success('Success', 'Billing info updated');
          }
        },
      });
  }

  // === STATUS ===

  completeSqd(): void {
    this.confirmationService.confirm({
      message: 'Are you sure you want to complete this dispatch? This will create inventory transactions and cannot be undone.',
      header: 'Confirm Completion',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.isLoading.set(true);
        this.proxyService
          .updateStatus(this.sqd()!.id, SecondQualityDispatchStatus.Completed)
          .pipe(finalize(() => this.isLoading.set(false)))
          .subscribe({
            next: (result) => {
              if (result?.data) {
                this.sqd.set(result.data);
                GlobalMessageUtil.success('Success', 'Dispatch completed successfully');
              }
            },
          });
      },
    });
  }

  cancelSqd(): void {
    this.confirmationService.confirm({
      message: 'Are you sure you want to cancel this dispatch?',
      header: 'Confirm Cancellation',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.isLoading.set(true);
        this.proxyService
          .updateStatus(this.sqd()!.id, SecondQualityDispatchStatus.Cancelled)
          .pipe(finalize(() => this.isLoading.set(false)))
          .subscribe({
            next: (result) => {
              if (result?.data) {
                this.sqd.set(result.data);
                GlobalMessageUtil.success('Success', 'Dispatch cancelled');
              }
            },
          });
      },
    });
  }

  // === INVOICE ===

  downloadInvoice(isPreInvoice: boolean): void {
    this.isLoading.set(true);
    this.proxyService.downloadCommercialInvoice(this.sqd()!.id, isPreInvoice).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${isPreInvoice ? 'PreInvoice' : 'Invoice'}_${this.sqd()!.number}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
      },
    });
  }

  // === NAVIGATION ===

  goBack(): void {
    this.router.navigate(['/second-quality-dispatches']);
  }
}
